// Automated Notification Service for Client Property Interest
// Handles notification creation, escalation rules, and task assignment

const ESCALATION_RULES = {
  high: {
    initialAssignment: 'senior_agent',
    escalateAfterMinutes: 15,
    escalateTo: 'supervisor',
    secondEscalationMinutes: 30,
    secondEscalateTo: 'manager'
  },
  medium: {
    initialAssignment: 'available_agent',
    escalateAfterMinutes: 60,
    escalateTo: 'senior_agent',
    secondEscalationMinutes: 180,
    secondEscalateTo: 'supervisor'
  },
  low: {
    initialAssignment: 'available_agent',
    escalateAfterMinutes: 240,
    escalateTo: 'senior_agent',
    secondEscalationMinutes: 480,
    secondEscalateTo: 'supervisor'
  }
};

const STAFF_ROLES = {
  available_agent: ['staff_1', 'staff_2', 'staff_3', 'staff_4'],
  senior_agent: ['staff_1', 'staff_2'],
  supervisor: ['supervisor_1'],
  manager: ['manager_1']
};

class NotificationService {
  constructor() {
    this.notifications = [];
    this.escalationTimers = new Map();
    this.listeners = [];
  }

  // Subscribe to notification updates
  subscribe(callback) {
    this.listeners?.push(callback);
    return () => {
      this.listeners = this.listeners?.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners?.forEach(callback => callback(this.notifications));
  }

  // Create notification when client expresses interest
  createInterestNotification(interestData) {
    const { clientName, clientRefNo, propertyTitle, propertyId, priority, phone, email, interestDate } = interestData;

    const escalationRule = ESCALATION_RULES?.[priority] || ESCALATION_RULES?.medium;
    const assignedStaff = this.assignStaff(escalationRule?.initialAssignment);

    const notification = {
      id: `ALT-${Date.now()}`,
      type: 'client_interest',
      title: `عميل جديد مهتم بعقار - ${propertyTitle}`,
      description: `العميل ${clientName} (${clientRefNo}) أبدى اهتماماً بالعقار. يتطلب متابعة فورية.`,
      priority: priority,
      status: 'unread',
      assignedTo: assignedStaff?.name,
      assignedToId: assignedStaff?.id,
      createdAt: new Date()?.toLocaleString('ar-JO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      dueDate: this.calculateDueDate(priority),
      clientName: clientName,
      clientRefNo: clientRefNo,
      propertyTitle: propertyTitle,
      propertyId: propertyId,
      phone: phone,
      email: email,
      interestDate: interestDate,
      escalated: false,
      escalationLevel: 0,
      comments: [],
      tasks: [
        {
          id: `TASK-${Date.now()}-1`,
          title: 'الاتصال بالعميل',
          description: 'إجراء مكالمة أولية للتعرف على احتياجات العميل',
          status: 'pending',
          priority: priority,
          dueDate: this.calculateTaskDueDate(priority, 30) // 30 minutes for high priority
        },
        {
          id: `TASK-${Date.now()}-2`,
          title: 'جدولة معاينة العقار',
          description: 'ترتيب موعد لمعاينة العقار مع العميل',
          status: 'pending',
          priority: priority,
          dueDate: this.calculateTaskDueDate(priority, 120) // 2 hours
        }
      ]
    };

    this.notifications?.unshift(notification);
    this.setupEscalation(notification, escalationRule);
    this.notifyListeners();

    // Return notification for immediate display
    return notification;
  }

  // Assign staff based on role and availability
  assignStaff(role) {
    const staffIds = STAFF_ROLES?.[role] || STAFF_ROLES?.available_agent;
    const randomIndex = Math.floor(Math.random() * staffIds?.length);
    const staffId = staffIds?.[randomIndex];

    const staffNames = {
      'staff_1': 'سارة أحمد',
      'staff_2': 'أحمد محمد',
      'staff_3': 'محمد خالد',
      'staff_4': 'فاطمة علي',
      'supervisor_1': 'مدير الفريق',
      'manager_1': 'المدير العام'
    };

    return {
      id: staffId,
      name: staffNames?.[staffId] || 'موظف'
    };
  }

  // Calculate due date based on priority
  calculateDueDate(priority) {
    const now = new Date();
    const minutesToAdd = {
      'high': 60,      // 1 hour
      'medium': 240,   // 4 hours
      'low': 480       // 8 hours
    };

    now?.setMinutes(now?.getMinutes() + (minutesToAdd?.[priority] || 240));
    return now?.toLocaleDateString('ar-JO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  // Calculate task due date
  calculateTaskDueDate(priority, baseMinutes) {
    const now = new Date();
    const multiplier = {
      'high': 1,
      'medium': 2,
      'low': 4
    };

    now?.setMinutes(now?.getMinutes() + (baseMinutes * (multiplier?.[priority] || 2)));
    return now?.toLocaleString('ar-JO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Setup escalation timers
  setupEscalation(notification, escalationRule) {
    // First escalation
    const firstTimer = setTimeout(() => {
      this.escalateNotification(notification?.id, escalationRule?.escalateTo, 1);
    }, escalationRule?.escalateAfterMinutes * 60 * 1000);

    // Second escalation
    const secondTimer = setTimeout(() => {
      this.escalateNotification(notification?.id, escalationRule?.secondEscalateTo, 2);
    }, escalationRule?.secondEscalationMinutes * 60 * 1000);

    this.escalationTimers?.set(notification?.id, [firstTimer, secondTimer]);
  }

  // Escalate notification to higher level
  escalateNotification(notificationId, escalateTo, level) {
    const notification = this.notifications?.find(n => n?.id === notificationId);
    if (!notification || notification?.status === 'read') return;

    const newStaff = this.assignStaff(escalateTo);
    notification.escalated = true;
    notification.escalationLevel = level;
    notification.assignedTo = newStaff?.name;
    notification.assignedToId = newStaff?.id;
    notification?.comments?.push({
      id: Date.now(),
      author: 'النظام',
      date: new Date()?.toLocaleString('ar-JO'),
      text: `تم تصعيد التنبيه إلى ${newStaff?.name} - المستوى ${level}`
    });

    this.notifyListeners();
  }

  // Clear escalation timers
  clearEscalation(notificationId) {
    const timers = this.escalationTimers?.get(notificationId);
    if (timers) {
      timers?.forEach(timer => clearTimeout(timer));
      this.escalationTimers?.delete(notificationId);
    }
  }

  // Update notification status
  updateNotificationStatus(notificationId, status) {
    const notification = this.notifications?.find(n => n?.id === notificationId);
    if (notification) {
      notification.status = status;
      if (status === 'read') {
        this.clearEscalation(notificationId);
      }
      this.notifyListeners();
    }
  }

  // Add comment to notification
  addComment(notificationId, comment) {
    const notification = this.notifications?.find(n => n?.id === notificationId);
    if (notification) {
      notification?.comments?.push({
        id: Date.now(),
        author: comment?.author,
        date: new Date()?.toLocaleString('ar-JO'),
        text: comment?.text
      });
      this.notifyListeners();
    }
  }

  // Update task status
  updateTaskStatus(notificationId, taskId, status) {
    const notification = this.notifications?.find(n => n?.id === notificationId);
    if (notification) {
      const task = notification?.tasks?.find(t => t?.id === taskId);
      if (task) {
        task.status = status;
        this.notifyListeners();
      }
    }
  }

  // Reassign notification
  reassignNotification(notificationId, staffId) {
    const notification = this.notifications?.find(n => n?.id === notificationId);
    if (notification) {
      const staffNames = {
        'staff_1': 'سارة أحمد',
        'staff_2': 'أحمد محمد',
        'staff_3': 'محمد خالد',
        'staff_4': 'فاطمة علي'
      };
      notification.assignedToId = staffId;
      notification.assignedTo = staffNames?.[staffId] || 'موظف';
      this.notifyListeners();
    }
  }

  // Get all notifications
  getAllNotifications() {
    return this.notifications;
  }

  // Get notifications by staff
  getNotificationsByStaff(staffId) {
    return this.notifications?.filter(n => n?.assignedToId === staffId);
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
